import React from 'react';
import { useMemo, useRef, useLayoutEffect } from 'react';
import { useAuthContext } from './features/auth/hooks';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useSettings } from './features/settings/hooks/useSettingsState';

let rendererModule = {
  status: 'pending',
  promise: null,
  result: null,
};

export default function lazyLegacyRoot(getLegacyComponent, customeCss) {
  let componentModule = {
    status: 'pending',
    promise: null,
    result: null,
  };

  return function Wrapper(props) {
    const createLegacyRoot = readModule(rendererModule, () =>
      import('../../legacy/src/createLegacyRoot')
    ).default;
    const Component = readModule(componentModule, getLegacyComponent).default;
    const containerRef = useRef(null);
    const rootRef = useRef(null);
    const { data: authState } = useAuthContext();
    const { data: settings = {} } = useSettings(undefined, { skip: !localStorage.getItem('token') });
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n } = useTranslation();
    const params = useParams();
    const pageId = location.pathname.split('/')[location.pathname.split('/').length - 1];
    location.state = {
      ...location.state,
      'pageId': Number(pageId) ? pageId : null,
      'ownerSlug': location.pathname.split('/')[2],
      'slug': location.pathname.split('/')[3]
    }
    const context = useMemo(() => ({
      language: i18n.language,
      settings,
      authState,
      navigate,
      params,
      location
    }), [authState, location, navigate, params, settings, i18n.language]);

    // Create/unmount.;
    useLayoutEffect(() => {
      //we need this code to clear cluttering console from prop-types warnings
      // Suppress noisy prop-types warning only for legacy subtree
      const originalConsoleError = console.error;
      console.error = function filteredConsoleError(...args) {
        try {
          const msg = args && args[0];
          if (
            typeof msg === 'string' &&
            msg.includes('Calling PropTypes validators directly is not supported by the `prop-types` package')
          ) {
            return; // swallow only this specific warning
          }
        } catch (_) {}
        return originalConsoleError.apply(console, args);
      };

      if (!rootRef.current) {
        rootRef.current = createLegacyRoot(containerRef.current);
      }
      const root = rootRef.current;
      function compUnmount() {
        root.unmount();
        // restore console.error
        console.error = originalConsoleError;
      }
      return () => {
        compUnmount();
      }
    }, [createLegacyRoot]);

    // Mount/update.
    useLayoutEffect(() => {
      if (rootRef.current) {
        rootRef.current.render(Component, props, context);
      }
    }, [Component, props, context]);

    const className = `modern-legacy-bridge ${customeCss ? customeCss : ''}`

    return <div className={className} style={{ height: '100vh' }} ref={containerRef} />;
  };
}

// This is similar to React.lazy, but implemented manually.
// We use this to Suspend rendering of this component until
// we fetch the component and the legacy React to render it.
function readModule(record, createPromise) {
  if (record.status === 'fulfilled') {
    return record.result;
  }
  if (record.status === 'rejected') {
    throw record.result;
  }
  if (!record.promise) {
    record.promise = createPromise().then(
      value => {
        if (record.status === 'pending') {
          record.status = 'fulfilled';
          record.promise = null;
          record.result = value;
        }
      },
      error => {
        if (record.status === 'pending') {
          record.status = 'rejected';
          record.promise = null;
          record.result = error;
        }
      }
    );
  }
  throw record.promise;
}
